import { mpClient } from "./client";
import { mpModeBridge } from "./mp-mode-bridge";
import {
  ensureMpConnected,
  ensureMultiplayerGameRunning,
  handleMpServerEvent,
  hideMpSpeedrunInGameChrome,
  isMultiplayerActive,
  releaseMultiplayerGamePointer,
  startMultiplayerGame,
  stopMultiplayerGame,
} from "./game-runtime";
import { addMpSpeedrunFinisher } from "./mp-speedrun-spectate";
import { remotePlayers } from "./remote-players";
import { MP_MAX_PLAYERS, MP_MIN_PLAYERS, mpIsRemoteWsUrl, mpWsUrl } from "../../../lib/multiplayer/protocol";
import { hideHubScreens } from "../game-hub";
import { syncToggleSwitch } from "../toggle-switch";
import type { MpFriendRoomEntry, MpGameMode } from "../../../lib/multiplayer/protocol";
import { getSpeedrunLevelCount } from "../speedrun-levels";
import { getDotsState } from "../dots-currency";
import { filterPlayCosmeticIds } from "../cursor-cosmetics";
import { isPlayTransitionEnabled, runPlayTransition, withExitPlayTransition } from "../play-transition";
import { isSoccerActive } from "../soccer";

export type MpUiHooks = {
  showEl: (el: HTMLElement | null) => void;
  hideEl: (el: HTMLElement | null) => void;
  openModes: () => void;
  playScreenEnterMotion: () => void;
  playSelectSound: () => void;
  playModeCardBeep: () => void;
  getPlayerName: () => string;
  getAccountUserId?: () => string | null;
  fetchFriends?: () => Promise<Array<{ id: string; displayName?: string | null }>>;
  stopPlaying: () => void;
  isPlaying: () => boolean;
  refreshMpSpeedrunChrome?: () => void;
  releaseGamePointer?: () => void;
  updateMusicState?: () => void;
};

const GUEST_NAME_KEY = "mpGuestName";

function resolveEquippedCosmeticIds(): string[] {
  return filterPlayCosmeticIds(getDotsState().equippedCosmeticIds);
}

function mpConnectionErrorMessage(err?: unknown): string {
  const url = mpClient.lastConnectUrl ?? mpWsUrl();
  const host = location.hostname;
  const code = err instanceof Error ? err.message : "";

  if (code === "connection_timeout" && mpIsRemoteWsUrl(url)) {
    return `Server did not respond in time (${url}). On Render free tier the first connection can take up to 60s — try again. Check the service is Live in Render dashboard.`;
  }
  if (code === "connection_timeout") {
    return "Connection timed out. Stop old terminals and run: npm run dev";
  }
  if (host === "localhost" || host === "127.0.0.1") {
    return "Multiplayer server not running. Stop any old dev server and run: npm run dev";
  }
  if (!import.meta.env.VITE_MP_WS_URL) {
    return `Multiplayer is not configured for this deploy. Set VITE_MP_WS_URL=wss://YOUR-SERVICE.onrender.com/ws/mp on Vercel and redeploy. (Currently trying ${url})`;
  }
  return `Could not connect to ${url}. Check Render service is running and VITE_MP_WS_URL uses wss://…/ws/mp`;
}

let hooks: MpUiHooks | null = null;

const mpEntryScreen = () => document.getElementById("mpEntryScreen");
const mpCreateScreen = () => document.getElementById("mpCreateScreen");
const mpJoinScreen = () => document.getElementById("mpJoinScreen");
const mpLobbyScreen = () => document.getElementById("mpLobbyScreen");
const mpGameModeScreen = () => document.getElementById("mpGameModeScreen");
const mpJoinCodeInput = () => document.getElementById("mpJoinCodeInput") as HTMLInputElement | null;
const mpJoinError = () => document.getElementById("mpJoinError");
const mpEntryError = () => document.getElementById("mpEntryError");
const mpCreateRoomBtn = () => document.getElementById("mpCreateRoomBtn") as HTMLButtonElement | null;
const mpCreateSubmitBtn = () => document.getElementById("mpCreateSubmitBtn") as HTMLButtonElement | null;
const mpCreatePreSection = () => document.getElementById("mpCreatePreSection");
const mpCreatePostSection = () => document.getElementById("mpCreatePostSection");
const mpAllowFriendsBtn = () => document.getElementById("mpAllowFriendsBtn") as HTMLButtonElement | null;
const mpAllowFriendsHint = () => document.getElementById("mpAllowFriendsHint");
const mpCreateContinueBtn = () => document.getElementById("mpCreateContinueBtn") as HTMLButtonElement | null;
const mpCreateRoomCode = () => document.getElementById("mpCreateRoomCode");
const mpCreateStatus = () => document.getElementById("mpCreateStatus");
const mpCreateError = () => document.getElementById("mpCreateError");
const mpInviteFriendsList = () => document.getElementById("mpInviteFriendsList");
const mpInviteFriendsHint = () => document.getElementById("mpInviteFriendsHint");
const mpFriendRoomsSection = () => document.getElementById("mpFriendRoomsSection");
const mpFriendRoomsList = () => document.getElementById("mpFriendRoomsList");
const mpLobbyCode = () => document.getElementById("mpLobbyCode");
const mpLobbyWaiting = () => document.getElementById("mpLobbyWaiting");
const mpLobbyPlayerList = () => document.getElementById("mpLobbyPlayerList");
const mpLobbyStartBtn = () => document.getElementById("mpLobbyStartBtn");
const mpSubModeHostHint = () => document.getElementById("mpSubModeHostHint");
const mpLobbyError = () => document.getElementById("mpLobbyError");
const mpResultBanner = () => document.getElementById("mpResultBanner");

const mpLobbyModes = () => document.getElementById("mpLobbyModes");
const mpGameSettingsHint = () => document.getElementById("mpGameSettingsHint");
const mpGameSettingsSpeedrun = () => document.getElementById("mpGameSettingsSpeedrun");
const mpGameSettingsSnake = () => document.getElementById("mpGameSettingsSnake");
const mpSpeedrunRaceCursorsBtn = () => document.getElementById("mpSpeedrunRaceCursorsBtn");
const mpSnakeBallsBtn = () => document.getElementById("mpSnakeBallsBtn");

let mpSpeedrunResultsTimer: ReturnType<typeof setTimeout> | null = null;
let mpGameOverFlowTimer: ReturnType<typeof setTimeout> | null = null;
let mpGameOverFlowStarted = false;
let pendingFriendRooms: MpFriendRoomEntry[] = [];
let createRoomInProgress = false;
let allowFriendJoin = true;
let createRoomDone = false;

export function initMultiplayerUi(h: MpUiHooks): void {
  hooks = h;

  document.getElementById("mpCreateRoomBtn")?.addEventListener("click", () => showCreateScreenPre());
  document.getElementById("mpCreateSubmitBtn")?.addEventListener("click", () => void submitCreateRoom());
  document.getElementById("mpAllowFriendsBtn")?.addEventListener("click", () => toggleAllowFriends());
  document.getElementById("mpJoinRoomBtn")?.addEventListener("click", () => void openJoinScreen());
  document.getElementById("mpCreateContinueBtn")?.addEventListener("click", () => showLobby());
  document.getElementById("mpCreateBackBtn")?.addEventListener("click", () => void cancelCreateScreen());
  document.getElementById("mpCreateCopyCodeBtn")?.addEventListener("click", () => void copyCreateRoomCode());
  document.getElementById("mpJoinSubmitBtn")?.addEventListener("click", () => void submitJoin());
  document.getElementById("mpJoinBackBtn")?.addEventListener("click", () => showMpEntry());
  document.getElementById("mpCopyCodeBtn")?.addEventListener("click", () => void copyRoomCode());
  document.getElementById("mpLobbyStartBtn")?.addEventListener("click", () => void hostOpenModeSetup());
  mpLobbyModes()?.addEventListener("click", (e) => {
    if (!mpClient.isHost()) return;
    const card = (e.target as HTMLElement).closest(".mpModeCard");
    if (!card) return;
    const mode = card.getAttribute("data-mode") as MpGameMode | null;
    if (mode) selectMpMode(mode);
  });
  mpGameModeScreen()?.addEventListener("click", (e) => {
    if (!mpClient.isHost()) return;
    const card = (e.target as HTMLElement).closest(".mpSubModeCard");
    if (!card) return;
    const sub = card.getAttribute("data-submode");
    if (sub === "ffa" || sub === "coop") selectSubMode(sub);
  });
  document.getElementById("mpSpeedrunContinueBtn")?.addEventListener("click", () => void hostContinueSpeedrunSetup());
  document.getElementById("mpSandboxContinueBtn")?.addEventListener("click", () => void hostConfirmSandboxAndContinue());
  document.getElementById("mpSpeedrunConfigBackBtn")?.addEventListener("click", () => void hostCancelModeConfig());
  document.getElementById("mpSandboxConfigBackBtn")?.addEventListener("click", () => void hostCancelModeConfig());
  document.getElementById("mpFfaBtn")?.addEventListener("click", () => selectSubMode("ffa"));
  document.getElementById("mpCoopBtn")?.addEventListener("click", () => selectSubMode("coop"));
  document.getElementById("mpGameModePlayBtn")?.addEventListener("click", (e) => {
    void hostStartGame({ x: e.clientX, y: e.clientY });
  });
  document.getElementById("mpGameModeBackBtn")?.addEventListener("click", () => void hostBackFromSubMode());
  mpSpeedrunRaceCursorsBtn()?.addEventListener("click", () => toggleSpeedrunRaceCursors());
  mpSnakeBallsBtn()?.addEventListener("click", () => toggleSnakeBalls());
  document.getElementById("mpReturnLobbyBtn")?.addEventListener("click", () => {
    if (mpClient.isHost()) mpClient.returnLobby();
  });

  document.getElementById("mpPlayAgainBtn")?.addEventListener("click", () => hostMpPlayAgain());
  document.getElementById("mpSpeedrunPlayAgainBtn")?.addEventListener("click", () => hostMpSpeedrunPlayAgain());
  document.getElementById("mpSpeedrunNextLevelBtn")?.addEventListener("click", () => hostMpSpeedrunNextLevel());

  mpJoinCodeInput()?.addEventListener("keydown", (e) => {
    if (e.key === "Enter") void submitJoin();
  });

  mpClient.onMessage((msg) => {
    if (msg.type === "error") {
      setEntryError(msg.message);
      setCreateError(msg.message);
      setJoinError(msg.message);
      setLobbyError(msg.message);
    }
    if (msg.type === "room_state") {
      if (mpCreateScreen() && !mpCreateScreen()!.classList.contains("hidden")) {
        const codeEl = mpCreateRoomCode();
        if (codeEl && msg.room.code) codeEl.textContent = msg.room.code;
        if (createRoomDone) showCreatePostSection();
      }
      renderLobby(msg.room, { skipScreenSync: isCreateScreenVisible() });
      if (msg.room.phase === "speedrun_level_select") {
        renderSpeedrunLevelMpChrome(msg.room);
      }
      if (msg.room.phase === "sandbox_config") {
        renderSandboxMpChrome(msg.room);
      }
      if (msg.room.phase === "submode_select") {
        renderSubModeScreen(msg.room);
      }
      if (msg.room.phase === "playing") {
        hideMpGameOverScreen();
        hideMpSpeedrunResultsScreen();
        if (msg.room.selectedMode === "soccer") {
          /* Cursor Soccer owns its own runtime. */
        } else if (hooks && (!hooks.isPlaying() || !isMultiplayerActive())) {
          void startMultiplayerGame();
        } else {
          ensureMultiplayerGameRunning();
        }
      }
      if (msg.room.phase === "results" && msg.room.selectedMode !== "soccer") {
        beginMpGameOverFlow(msg.room.resultMessage ?? "Game over");
      }
      if (msg.room.phase === "speedrun_results") {
        beginMpSpeedrunResultsFlow(msg.room.speedrunResults ?? [], msg.room.speedrunLevel ?? 1);
      }
      if (msg.room.phase === "submode_select") {
        hideMpGameOverScreen();
        hideMpSpeedrunResultsScreen();
      }
      if (msg.room.phase === "lobby") {
        hideMpGameOverScreen();
        hideMpSpeedrunResultsScreen();
        if (isSoccerActive() || msg.room.selectedMode === "soccer") {
          return;
        }
        if (
          document.body.classList.contains("game-active") ||
          hooks?.isPlaying() ||
          isMultiplayerActive()
        ) {
          endMultiplayerToLobby();
        }
      }
    }
    if (msg.type === "friend_rooms") {
      pendingFriendRooms = msg.rooms;
      renderFriendRoomsOnJoin(msg.rooms);
      return;
    }
    if (msg.type === "speedrun_finisher" && msg.id && msg.name != null && msg.timeMs != null) {
      addMpSpeedrunFinisher({ id: msg.id, name: msg.name, timeMs: msg.timeMs });
      hooks?.refreshMpSpeedrunChrome?.();
    }
    if (msg.type === "speedrun_results") {
      beginMpSpeedrunResultsFlow(msg.results, msg.level);
      return;
    }
    if (msg.type === "match_to_lobby") {
      endMultiplayerToLobby();
      return;
    }
    if (msg.type === "kicked") {
      stopMultiplayerGame();
      leaveMpScreens();
      disconnectMpSocketLeavingHub();
      hooks?.openModes();
      alert("You were kicked from the room.");
      return;
    }
    if (msg.type === "game_start") {
      hideAllMpScreens();
      syncMpLeaveButtons();
      if (msg.mode !== "soccer") void startMultiplayerGame();
    }
    if (msg.type === "game_over") {
      if (isSoccerActive() || mpClient.gameMode === "soccer" || mpClient.room?.selectedMode === "soccer") {
        return;
      }
      beginMpGameOverFlow(msg.message ?? "Game over");
    }
    handleMpServerEvent(msg);
  });
}

export function hideAllMpScreens(): void {
  hooks?.hideEl(mpEntryScreen());
  hooks?.hideEl(mpCreateScreen());
  hooks?.hideEl(mpJoinScreen());
  hooks?.hideEl(mpLobbyScreen());
  hooks?.hideEl(mpGameModeScreen());
  document.getElementById("soccerOverlay")?.classList.add("hidden");
}

export function isMpScreenVisible(): boolean {
  return [mpEntryScreen(), mpCreateScreen(), mpJoinScreen(), mpLobbyScreen(), mpGameModeScreen()].some(
    (el) => el && !el.classList.contains("hidden"),
  );
}

export function handleMpReturn(): boolean {
  const gameOverScreen = document.getElementById("mpGameOverScreen");
  const resultsScreen = document.getElementById("mpSpeedrunResultsScreen");
  const resultsPending = mpSpeedrunResultsTimer != null || mpGameOverFlowTimer != null;
  if (
    (gameOverScreen && !gameOverScreen.classList.contains("hidden")) ||
    (resultsScreen && !resultsScreen.classList.contains("hidden")) ||
    resultsPending
  ) {
    withExitPlayTransition(() => {
      hideMpGameOverScreen();
      hideMpSpeedrunResultsScreen();
      stopMultiplayerGame();
      hooks?.stopPlaying();
      document.body.classList.remove("game-active");
      if (mpClient.isHost()) mpClient.returnLobby();
      else showLobby();
    });
    return true;
  }
  if (hooks?.isPlaying() && hooks?.getGameMode?.() === "multiplayer" && mpClient.room) {
    withExitPlayTransition(() => {
      mpClient.exitToLobby();
      releaseMultiplayerGamePointer();
      stopMultiplayerGame();
      hooks?.stopPlaying();
      showLobby();
    });
    return true;
  }
  if (document.getElementById("speedrunLevelScreen") && !document.getElementById("speedrunLevelScreen")!.classList.contains("hidden")) {
    if (mpClient.room?.phase === "speedrun_level_select") {
      if (mpClient.isHost()) void hostCancelModeConfig();
      else showLobbyFromSync();
      return true;
    }
    return false;
  }
  if (document.getElementById("sandboxScreen") && !document.getElementById("sandboxScreen")!.classList.contains("hidden")) {
    if (mpClient.room?.phase === "sandbox_config") {
      if (mpClient.isHost()) void hostCancelModeConfig();
      else showLobbyFromSync();
      return true;
    }
    return false;
  }
  if (mpGameModeScreen() && !mpGameModeScreen()!.classList.contains("hidden")) {
    if (mpClient.room?.phase === "submode_select") {
      if (mpClient.isHost()) void hostBackFromSubMode();
      else showLobbyFromSync();
      return true;
    }
    return false;
  }
  if (mpJoinScreen() && !mpJoinScreen()!.classList.contains("hidden")) {
    showMpEntry();
    return true;
  }
  if (mpCreateScreen() && !mpCreateScreen()!.classList.contains("hidden")) {
    void cancelCreateScreen();
    return true;
  }
  if (mpLobbyScreen() && !mpLobbyScreen()!.classList.contains("hidden")) {
    withExitPlayTransition(() => {
      void leaveLobby();
    });
    return true;
  }
  if (mpEntryScreen() && !mpEntryScreen()!.classList.contains("hidden")) {
    withExitPlayTransition(() => {
      leaveMpScreens();
      disconnectMpSocketLeavingHub();
      hooks?.openModes();
    });
    return true;
  }
  return false;
}

function leaveMpScreens(): void {
  hideAllMpScreens();
}

export async function openMpEntry(): Promise<void> {
  hooks?.hideEl(document.getElementById("modeScreen"));
  hideHubScreens();
  hooks?.showEl(mpEntryScreen());
  hooks?.showEl(document.getElementById("returnBtn"));
  hooks?.playScreenEnterMotion();
  hooks?.updateMusicState?.();
  clearMpErrors();
}

function showMpEntry(): void {
  clearMpErrors();
  hooks?.hideEl(mpJoinScreen());
  hooks?.hideEl(mpCreateScreen());
  hooks?.hideEl(mpLobbyScreen());
  hooks?.showEl(mpEntryScreen());
}

function isCreateScreenVisible(): boolean {
  return !!(mpCreateScreen() && !mpCreateScreen()!.classList.contains("hidden"));
}

function showCreateScreenPre(): void {
  clearMpErrors();
  createRoomDone = false;
  allowFriendJoin = !!hooks?.getAccountUserId?.();
  hooks?.hideEl(mpEntryScreen());
  hooks?.showEl(mpCreateScreen());
  hooks?.showEl(document.getElementById("returnBtn"));
  hooks?.playScreenEnterMotion();
  mpCreatePreSection()?.classList.remove("hidden");
  mpCreatePostSection()?.classList.add("hidden");
  if (mpCreateRoomCode()) mpCreateRoomCode()!.textContent = "—";
  setCreateStatus("");
  syncAllowFriendsToggle();
  void renderInviteFriendsList();
}

function showCreatePostSection(): void {
  mpCreatePreSection()?.classList.add("hidden");
  mpCreatePostSection()?.classList.remove("hidden");
}

function syncAllowFriendsToggle(): void {
  const btn = mpAllowFriendsBtn();
  const hint = mpAllowFriendsHint();
  const signedIn = !!hooks?.getAccountUserId?.();
  if (btn) {
    syncToggleSwitch(btn, allowFriendJoin && signedIn);
    btn.disabled = !signedIn;
  }
  if (hint) {
    hint.textContent = signedIn
      ? allowFriendJoin
        ? "Friends will see this room under Join Room."
        : "Only players with the room code can join."
      : "Sign in to let friends discover your room from Join Room.";
  }
}

function toggleAllowFriends(): void {
  if (!hooks?.getAccountUserId?.()) return;
  hooks?.playModeCardBeep();
  allowFriendJoin = !allowFriendJoin;
  syncAllowFriendsToggle();
}

async function submitCreateRoom(): Promise<void> {
  if (createRoomInProgress || createRoomDone) return;
  clearMpErrors();
  createRoomInProgress = true;
  const btn = mpCreateSubmitBtn();
  if (btn) {
    btn.disabled = true;
    btn.textContent = "Creating…";
  }
  try {
    await connect();
    const accountId =
      allowFriendJoin && hooks?.getAccountUserId?.() ? hooks.getAccountUserId()! : null;
    if (!mpClient.createRoom(resolvePlayerName(), accountId, resolveEquippedCosmeticIds())) {
      throw new Error("send failed");
    }
    await waitForRoom();
    createRoomDone = true;
    const code = mpClient.room?.code ?? "";
    if (mpCreateRoomCode()) mpCreateRoomCode()!.textContent = code;
    setCreateStatus(
      allowFriendJoin && accountId
        ? "Room created — friends can join from Join Room, or share the code."
        : "Room created — share the code with players you want to invite.",
    );
    showCreatePostSection();
    void renderInviteFriendsList();
  } catch (err) {
    setCreateError(
      err instanceof Error && err.message === "timeout"
        ? "Room creation timed out. Try again."
        : mpConnectionErrorMessage(err),
    );
  } finally {
    createRoomInProgress = false;
    if (btn) {
      btn.disabled = false;
      btn.textContent = "Create Room";
    }
  }
}

async function cancelCreateScreen(): Promise<void> {
  if (mpClient.room) {
    mpClient.leaveRoom();
    remotePlayers.clear();
  }
  createRoomInProgress = false;
  createRoomDone = false;
  showMpEntry();
}

async function openJoinScreen(): Promise<void> {
  clearMpErrors();
  hooks?.hideEl(mpEntryScreen());
  hooks?.showEl(mpJoinScreen());
  hooks?.showEl(document.getElementById("returnBtn"));
  hooks?.playScreenEnterMotion();
  const inp = mpJoinCodeInput();
  if (inp) {
    inp.value = "";
    inp.focus();
  }
  pendingFriendRooms = [];
  mpFriendRoomsSection()?.classList.add("hidden");
  if (mpFriendRoomsList()) mpFriendRoomsList()!.replaceChildren();
  try {
    await connect();
    await refreshFriendRoomsOnJoin();
  } catch {
    /* code join still works without friend list */
  }
}

function showJoinScreen(): void {
  void openJoinScreen();
}

async function connect(): Promise<void> {
  try {
    await ensureMpConnected();
  } catch (err) {
    const msg = mpConnectionErrorMessage(err);
    setEntryError(msg);
    setJoinError(msg);
    setLobbyError(msg);
    throw err;
  }
}

async function createRoom(): Promise<void> {
  await openCreateScreen();
}

async function refreshFriendRoomsOnJoin(): Promise<void> {
  const friends = (await hooks?.fetchFriends?.()) ?? [];
  const ids = friends.map((f) => f.id).filter(Boolean);
  if (ids.length === 0) return;
  mpClient.listFriendRooms(ids);
}

function renderFriendRoomsOnJoin(rooms: MpFriendRoomEntry[]): void {
  const section = mpFriendRoomsSection();
  const list = mpFriendRoomsList();
  if (!section || !list) return;
  list.replaceChildren();
  if (rooms.length === 0) {
    section.classList.add("hidden");
    return;
  }
  section.classList.remove("hidden");
  for (const room of rooms) {
    const li = document.createElement("li");
    li.className = "mpFriendRoomRow";
    const meta = document.createElement("div");
    meta.className = "mpFriendRoomMeta";
    const host = document.createElement("span");
    host.className = "mpFriendRoomHost";
    host.textContent = room.hostName;
    const sub = document.createElement("span");
    sub.className = "mpFriendRoomSub";
    sub.textContent = `Room ${room.code} · ${room.playerCount} player${room.playerCount === 1 ? "" : "s"}`;
    meta.appendChild(host);
    meta.appendChild(sub);
    const joinBtn = document.createElement("button");
    joinBtn.type = "button";
    joinBtn.className = "btn green";
    joinBtn.textContent = "Join";
    joinBtn.onclick = () => void joinFriendRoom(room.code);
    li.appendChild(meta);
    li.appendChild(joinBtn);
    list.appendChild(li);
  }
}

async function joinFriendRoom(code: string): Promise<void> {
  clearMpErrors();
  try {
    await connect();
    if (!mpClient.joinRoom(code, resolvePlayerName(), resolveEquippedCosmeticIds())) {
      throw new Error("send failed");
    }
    await waitForRoom();
    showLobby();
  } catch (err) {
    setJoinError(
      err instanceof Error && err.message === "timeout"
        ? "Join timed out. The room may have closed."
        : "Could not join the room. Try again.",
    );
  }
}

async function renderInviteFriendsList(): Promise<void> {
  const list = mpInviteFriendsList();
  const hint = mpInviteFriendsHint();
  if (!list || !hint) return;
  list.replaceChildren();
  const accountId = hooks?.getAccountUserId?.();
  if (!createRoomDone) return;
  if (!allowFriendJoin || !accountId) {
    hint.textContent = "Friend discovery is OFF for this room — share the code manually.";
    return;
  }
  hint.textContent = "These friends can join from Join Room → Friends' rooms.";
  const friends = (await hooks.fetchFriends?.()) ?? [];
  if (friends.length === 0) {
    const p = document.createElement("li");
    p.className = "accessHint";
    p.textContent = "No friends yet — add friends from Social.";
    list.appendChild(p);
    return;
  }
  for (const f of friends) {
    const li = document.createElement("li");
    li.className = "mpPlayerRow";
    const name = document.createElement("span");
    name.className = "mpPlayerName";
    name.textContent = f.displayName || "Friend";
    li.appendChild(name);
    list.appendChild(li);
  }
}

async function copyCreateRoomCode(): Promise<void> {
  const code = mpClient.room?.code ?? mpCreateRoomCode()?.textContent ?? "";
  if (!code || code === "—") return;
  try {
    await navigator.clipboard.writeText(code);
  } catch {
    /* ignore */
  }
}

async function submitJoin(): Promise<void> {
  const code = mpJoinCodeInput()?.value?.trim() ?? "";
  if (code.length !== 5) {
    setJoinError("Enter a 5-character room code.");
    return;
  }
  clearMpErrors();
  try {
    await connect();
    if (!mpClient.joinRoom(code, resolvePlayerName(), resolveEquippedCosmeticIds())) {
      throw new Error("send failed");
    }
    await waitForRoom();
    showLobby();
  } catch (err) {
    if (!mpJoinError()?.textContent) {
      setJoinError(err instanceof Error && err.message === "timeout"
        ? "Join timed out. Check the code and that the host room is still open."
        : "Could not join the room. Try again.");
    }
  }
}

function waitForRoom(): Promise<void> {
  return new Promise((resolve, reject) => {
    const t0 = Date.now();
    const check = () => {
      if (mpClient.room) {
        resolve();
        return;
      }
      if (Date.now() - t0 > 15000) {
        reject(new Error("timeout"));
        return;
      }
      requestAnimationFrame(check);
    };
    check();
  });
}

function showLobby(): void {
  createRoomDone = false;
  hooks?.hideEl(mpEntryScreen());
  hooks?.hideEl(mpCreateScreen());
  hooks?.hideEl(mpJoinScreen());
  hooks?.hideEl(mpGameModeScreen());
  hooks?.hideEl(document.getElementById("modeScreen"));
  hooks?.showEl(mpLobbyScreen());
  hooks?.showEl(document.getElementById("returnBtn"));
  mpModeBridge()?.setSandboxMpHostOnly(true);
  renderLobby(mpClient.room);
  syncMpLeaveButtons();
  hooks?.playScreenEnterMotion();
  hooks?.updateMusicState?.();
}

function hostOpenModeSetup(): void {
  if (!mpClient.isHost()) return;
  if (!mpClient.room?.selectedMode) {
    setLobbyError("Select a game mode first.");
    return;
  }
  setLobbyError("");
  mpClient.openModeSetup();
}

async function hostContinueSpeedrunSetup(): Promise<void> {
  if (!mpClient.isHost()) return;
  if (!mpClient.room?.speedrunLevel) return;
  setLobbyError("");
  hooks?.playModeCardBeep();
  mpClient.openSubModeSelect();
}

function formatMpSpeedrunTime(ms: number): string {
  const sec = ms / 1000;
  if (sec >= 60) {
    const m = Math.floor(sec / 60);
    const s = sec - m * 60;
    return `${m}:${s.toFixed(2).padStart(5, "0")}`;
  }
  return `${sec.toFixed(2)}s`;
}

function beginMpGameOverFlow(message: string): void {
  if (mpGameOverFlowStarted) return;
  mpGameOverFlowStarted = true;
  hideMpSpeedrunInGameChrome();
  hooks?.releaseGamePointer?.();
  releaseMultiplayerGamePointer();
  hooks?.stopPlaying();
  document.body.classList.remove("game-active");
  document.body.classList.remove("hideCursor");
  document.body.classList.add("mp-results-active");
  hooks?.showEl(document.getElementById("returnBtn"));

  if (mpGameOverFlowTimer != null) {
    clearTimeout(mpGameOverFlowTimer);
    mpGameOverFlowTimer = null;
  }
  mpGameOverFlowTimer = window.setTimeout(() => {
    mpGameOverFlowTimer = null;
    stopMultiplayerGame();
    showMpGameOverScreen(message);
  }, 1500);
}

function showMpGameOverScreen(message: string): void {
  hideMpSpeedrunInGameChrome();
  const screen = document.getElementById("mpGameOverScreen");
  const msgEl = document.getElementById("mpGameOverMessage");
  const playBtn = document.getElementById("mpPlayAgainBtn") as HTMLButtonElement | null;
  const hostHint = document.getElementById("mpGameOverHostHint");
  if (msgEl) msgEl.textContent = message;
  const host = mpClient.isHost();
  if (playBtn) playBtn.disabled = !host;
  if (hostHint) {
    if (host) hostHint.classList.add("hidden");
    else {
      hostHint.textContent = "Waiting for host to start a rematch…";
      hostHint.classList.remove("hidden");
    }
  }
  hooks?.hideEl(mpLobbyScreen());
  hooks?.hideEl(mpGameModeScreen());
  hooks?.showEl(screen);
  hooks?.showEl(document.getElementById("returnBtn"));
  document.body.classList.remove("game-active");
  document.body.classList.add("mp-results-active");
  const returnBtn = document.getElementById("returnBtn");
  if (returnBtn) {
    returnBtn.style.opacity = "1";
    returnBtn.style.pointerEvents = "auto";
  }
}

function hideMpGameOverScreen(): void {
  if (mpGameOverFlowTimer != null) {
    clearTimeout(mpGameOverFlowTimer);
    mpGameOverFlowTimer = null;
  }
  mpGameOverFlowStarted = false;
  document.body.classList.remove("mp-results-active");
  hooks?.hideEl(document.getElementById("mpGameOverScreen"));
}

function hostMpPlayAgain(): void {
  if (!mpClient.isHost()) return;
  hooks?.playModeCardBeep();
  hideMpGameOverScreen();
  mpClient.replayMatch();
}

function hostMpSpeedrunPlayAgain(): void {
  if (!mpClient.isHost()) return;
  hooks?.playModeCardBeep();
  hideMpSpeedrunResultsScreen();
  mpClient.replayMatch();
}

function hostMpSpeedrunNextLevel(): void {
  if (!mpClient.isHost()) return;
  hooks?.playModeCardBeep();
  hideMpSpeedrunResultsScreen();
  mpClient.nextSpeedrunLevel();
}

function beginMpSpeedrunResultsFlow(
  results: import("../../../lib/multiplayer/protocol").MpSpeedrunResultEntry[],
  level: number,
): void {
  if (mpSpeedrunResultsTimer != null) return;
  hideMpSpeedrunInGameChrome();
  hooks?.releaseGamePointer?.();
  releaseMultiplayerGamePointer();
  hooks?.stopPlaying();
  document.body.classList.remove("game-active");
  document.body.classList.remove("hideCursor");
  document.body.classList.add("mp-results-active");
  hooks?.showEl(document.getElementById("returnBtn"));

  mpSpeedrunResultsTimer = window.setTimeout(() => {
    mpSpeedrunResultsTimer = null;
    stopMultiplayerGame();
    showMpSpeedrunResultsScreen(results, level);
  }, 2000);
}

function showMpSpeedrunResultsScreen(
  results: import("../../../lib/multiplayer/protocol").MpSpeedrunResultEntry[],
  level: number,
): void {
  hideMpSpeedrunInGameChrome();
  const screen = document.getElementById("mpSpeedrunResultsScreen");
  const table = document.getElementById("mpSpeedrunResultsTable");
  const title = document.getElementById("mpSpeedrunResultsTitle");
  const hint = document.getElementById("mpSpeedrunResultsHint");
  const playBtn = document.getElementById("mpSpeedrunPlayAgainBtn") as HTMLButtonElement | null;
  const nextBtn = document.getElementById("mpSpeedrunNextLevelBtn") as HTMLButtonElement | null;
  if (title) title.textContent = `Level ${level} — Race Results`;
  if (table) {
    table.replaceChildren();
    const thead = document.createElement("thead");
    thead.innerHTML = "<tr><th>#</th><th>Player</th><th>Time</th><th>Device</th></tr>";
    table.appendChild(thead);
    const tbody = document.createElement("tbody");
    for (const row of results) {
      const tr = document.createElement("tr");
      tr.innerHTML = `<td>${row.rank}</td><td>${row.name}</td><td>${formatMpSpeedrunTime(row.timeMs)}</td><td>${row.device === "mobile" ? "Mobile" : "PC"}</td>`;
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
  }
  const host = mpClient.isHost();
  const atMaxLevel = level >= getSpeedrunLevelCount();
  if (playBtn) playBtn.disabled = !host;
  if (nextBtn) {
    nextBtn.classList.toggle("hidden", !host || atMaxLevel);
    nextBtn.disabled = !host || atMaxLevel;
  }
  if (hint) {
    if (host) hint.classList.add("hidden");
    else {
      hint.textContent = "Waiting for host…";
      hint.classList.remove("hidden");
    }
  }
  hooks?.hideEl(mpLobbyScreen());
  hooks?.hideEl(mpGameModeScreen());
  document.body.classList.remove("game-active");
  document.body.classList.add("mp-results-active");
  hooks?.showEl(screen);
  hooks?.showEl(document.getElementById("returnBtn"));
  const returnBtn = document.getElementById("returnBtn");
  if (returnBtn) {
    returnBtn.style.opacity = "1";
    returnBtn.style.pointerEvents = "auto";
  }
}

function hideMpSpeedrunResultsScreen(): void {
  if (mpSpeedrunResultsTimer != null) {
    clearTimeout(mpSpeedrunResultsTimer);
    mpSpeedrunResultsTimer = null;
  }
  document.body.classList.remove("mp-results-active");
  hooks?.hideEl(document.getElementById("mpSpeedrunResultsScreen"));
}

function hostOpenSubMode(): void {
  if (!mpClient.isHost()) return;
  mpClient.openSubModeSelect();
}

function hostConfirmSandboxAndContinue(): void {
  if (!mpClient.isHost()) return;
  const bridge = mpModeBridge();
  if (!bridge) return;
  mpClient.confirmSandboxConfig(bridge.collectSandboxConfigWire());
  mpClient.openSubModeSelect();
}

function hostCancelModeConfig(): void {
  if (!mpClient.isHost()) return;
  mpClient.cancelModeConfig();
}

function showLobbyFromSync(): void {
  hooks?.hideEl(mpGameModeScreen());
  hooks?.hideEl(document.getElementById("speedrunLevelScreen"));
  hooks?.hideEl(document.getElementById("sandboxScreen"));
  hooks?.showEl(mpLobbyScreen());
  hooks?.showEl(document.getElementById("returnBtn"));
  document.getElementById("sandboxPlayBtn")?.classList.remove("hidden");
  hooks?.updateMusicState?.();
}

function hostBackFromSubMode(): void {
  if (!mpClient.isHost()) return;
  mpClient.cancelSubModeSelect();
}

function selectMpMode(mode: MpGameMode): void {
  if (!mpClient.isHost()) return;
  hooks?.playModeCardBeep();
  mpClient.selectMode(mode);
  setLobbyError("");
}

function toggleSpeedrunRaceCursors(): void {
  if (!mpClient.isHost() || !mpClient.room || mpClient.room.phase !== "lobby") return;
  if (mpClient.room.selectedMode !== "speedrun") return;
  hooks?.playModeCardBeep();
  const next = mpClient.room.speedrunRaceShowCursors === false;
  mpClient.setSpeedrunRaceShowCursors(next);
}

function toggleSnakeBalls(): void {
  if (!mpClient.isHost() || !mpClient.room || mpClient.room.phase !== "lobby") return;
  if (mpClient.room.selectedMode !== "snake") return;
  hooks?.playModeCardBeep();
  const next = mpClient.room.snakeBallsEnabled === false;
  mpClient.setSnakeBalls(next);
}

function renderGameSettings(room: NonNullable<typeof mpClient.room>): void {
  const hint = mpGameSettingsHint();
  const speedrunBlock = mpGameSettingsSpeedrun();
  const snakeBlock = mpGameSettingsSnake();
  const speedrunBtn = mpSpeedrunRaceCursorsBtn() as HTMLButtonElement | null;
  const snakeBtn = mpSnakeBallsBtn() as HTMLButtonElement | null;
  if (!hint || !speedrunBlock || !snakeBlock) return;

  const host = mpClient.isHost();
  const inLobby = room.phase === "lobby";
  const mode = room.selectedMode;

  speedrunBlock.classList.add("hidden");
  snakeBlock.classList.add("hidden");

  if (!mode) {
    hint.textContent = "Select a Mode";
    hint.classList.remove("hidden");
    return;
  }

  if (mode === "speedrun") {
    hint.classList.add("hidden");
    speedrunBlock.classList.remove("hidden");
    const show = room.speedrunRaceShowCursors !== false;
    if (speedrunBtn) {
      syncToggleSwitch(speedrunBtn, show);
      speedrunBtn.disabled = !host || !inLobby;
    }
    return;
  }

  if (mode === "snake") {
    hint.classList.add("hidden");
    snakeBlock.classList.remove("hidden");
    const enabled = room.snakeBallsEnabled !== false;
    if (snakeBtn) {
      syncToggleSwitch(snakeBtn, enabled);
      snakeBtn.disabled = !host || !inLobby;
    }
    return;
  }

  hint.textContent = "This mode has no configurable settings.";
  hint.classList.remove("hidden");
}

function syncMpScreensFromRoom(room: NonNullable<typeof mpClient.room>): void {
  if (!hooks || hooks.isPlaying()) return;
  const bridge = mpModeBridge();

  if (room.phase === "speedrun_level_select") {
    hooks.hideEl(mpEntryScreen());
    hooks.hideEl(mpCreateScreen());
    hooks.hideEl(mpJoinScreen());
    hooks.hideEl(mpLobbyScreen());
    hooks.hideEl(mpGameModeScreen());
    hooks.hideEl(document.getElementById("sandboxScreen"));
    bridge?.openMpSpeedrunLevelScreen();
    bridge?.buildSpeedrunLevelGridForMp(
      (level) => {
        if (mpClient.isHost()) mpClient.selectSpeedrunLevel(level);
      },
      room.speedrunLevel,
    );
    renderSpeedrunLevelMpChrome(room);
    syncMpLeaveButtons();
    return;
  }

  if (room.phase === "sandbox_config") {
    hooks.hideEl(mpEntryScreen());
    hooks.hideEl(mpCreateScreen());
    hooks.hideEl(mpJoinScreen());
    hooks.hideEl(mpLobbyScreen());
    hooks.hideEl(mpGameModeScreen());
    hooks.hideEl(document.getElementById("speedrunLevelScreen"));
    bridge?.openMpSandboxScreen();
    if (room.sandboxConfig) bridge?.applySandboxConfigWire(room.sandboxConfig);
    renderSandboxMpChrome(room);
    syncMpLeaveButtons();
    return;
  }

  if (room.phase === "soccer_team_select") {
    hooks.hideEl(mpEntryScreen());
    hooks.hideEl(mpCreateScreen());
    hooks.hideEl(mpJoinScreen());
    hooks.hideEl(mpLobbyScreen());
    hooks.hideEl(mpGameModeScreen());
    hooks.hideEl(document.getElementById("speedrunLevelScreen"));
    hooks.hideEl(document.getElementById("sandboxScreen"));
    syncMpLeaveButtons();
    return;
  }

  if (room.phase === "submode_select") {
    hooks.hideEl(mpEntryScreen());
    hooks.hideEl(mpCreateScreen());
    hooks.hideEl(mpJoinScreen());
    hooks.hideEl(mpLobbyScreen());
    hooks.hideEl(document.getElementById("speedrunLevelScreen"));
    hooks.hideEl(document.getElementById("sandboxScreen"));
    hooks.showEl(mpGameModeScreen());
    hooks.showEl(document.getElementById("returnBtn"));
    renderSubModeScreen(room);
    syncMpLeaveButtons();
    return;
  }

  hooks.hideEl(mpGameModeScreen());
  hooks.hideEl(document.getElementById("speedrunLevelScreen"));
  hooks.hideEl(document.getElementById("sandboxScreen"));

  if (room.phase === "lobby" || room.phase === "results") {
    hooks.hideEl(mpEntryScreen());
    hooks.hideEl(mpCreateScreen());
    hooks.hideEl(mpJoinScreen());
    hooks.showEl(mpLobbyScreen());
    hooks.showEl(document.getElementById("returnBtn"));
    document.getElementById("sandboxPlayBtn")?.classList.remove("hidden");
    syncMpLeaveButtons();
  }
  hooks?.updateMusicState?.();
}

function syncMpLeaveButtons(): void {
  document.getElementById("mpFloatingLeaveBtn")?.classList.add("hidden");
  document.querySelectorAll(".mpRoomLeaveBtn").forEach((btn) => {
    btn.classList.add("hidden");
  });
}

function renderSpeedrunLevelMpChrome(room: NonNullable<typeof mpClient.room>): void {
  const host = mpClient.isHost();
  const cont = document.getElementById("mpSpeedrunContinueBtn");
  if (cont) {
    cont.classList.toggle("hidden", !host);
    cont.disabled = !host || !room.speedrunLevel;
    cont.textContent = "Continue";
  }
  const back = document.getElementById("mpSpeedrunConfigBackBtn");
  if (back) back.classList.toggle("hidden", !host);
  const hint = document.getElementById("mpSpeedrunHostHint");
  if (hint) {
    if (host) hint.classList.add("hidden");
    else {
      hint.textContent = room.speedrunLevel
        ? `Host selected Level ${room.speedrunLevel}. Waiting for Race or Co-op…`
        : "Host is choosing a level…";
      hint.classList.remove("hidden");
    }
  }
}

function renderSandboxMpChrome(room: NonNullable<typeof mpClient.room>): void {
  const host = mpClient.isHost();
  mpModeBridge()?.setSandboxMpHostOnly(host);
  const cont = document.getElementById("mpSandboxContinueBtn");
  if (cont) {
    cont.classList.toggle("hidden", !host);
    cont.disabled = !host;
  }
  const play = document.getElementById("sandboxPlayBtn");
  if (play) play.classList.toggle("hidden", true);
  const back = document.getElementById("mpSandboxConfigBackBtn");
  if (back) back.classList.toggle("hidden", !host);
  const hint = document.getElementById("mpSandboxHostHint");
  if (hint) {
    if (host) hint.classList.add("hidden");
    else {
      hint.textContent = room.sandboxConfig
        ? "Host configured sandbox. Waiting for Play…"
        : "Host is customizing sandbox…";
      hint.classList.remove("hidden");
    }
  }
}

function renderSubModeScreen(room: NonNullable<typeof mpClient.room>): void {
  const host = mpClient.isHost();
  const modeLabel =
    room.selectedMode === "normal"
      ? "NORMAL"
      : room.selectedMode === "wasd"
      ? "WASD"
      : room.selectedMode === "sandbox"
        ? "SANDBOX"
        : room.selectedMode === "speedrun"
          ? "SPEEDRUN"
          : room.selectedMode === "highway"
            ? "HIGHWAY"
            : room.selectedMode === "snake"
              ? "SNAKE"
            : "Game";

  const title = mpGameModeScreen()?.querySelector("h1");
  if (title) {
    title.textContent =
      room.selectedMode === "speedrun"
        ? `SPEEDRUN — Level ${room.speedrunLevel ?? "?"}`
        : `${modeLabel} — GAME MODE`;
  }

  const ffaCard = document.querySelector('.mpSubModeCard[data-submode="ffa"]');
  const coopCard = document.querySelector('.mpSubModeCard[data-submode="coop"]');
  if (room.selectedMode === "speedrun") {
    if (ffaCard) {
      const h = ffaCard.querySelector("h3");
      const p = ffaCard.querySelector("p");
      if (h) h.textContent = "Race";
      if (p) {
        p.innerHTML =
          "Each player races alone.<br>You only see your own run.<br>Finish times are ranked individually.";
      }
    }
    if (coopCard) {
      const h = coopCard.querySelector("h3");
      const p = coopCard.querySelector("p");
      if (h) h.textContent = "Co-op";
      if (p) {
        p.innerHTML =
          "Team mode.<br>See every teammate, shared lasers and points.<br>If one player hits a laser, everyone resets.<br>All must reach the goal — one team time.";
      }
    }
  } else {
    if (ffaCard) {
      const h = ffaCard.querySelector("h3");
      const p = ffaCard.querySelector("p");
      if (h) h.textContent = "FFA";
      if (p) {
        p.innerHTML =
          "Free for all.<br>When you die, you spectate.<br>Last player alive wins.";
      }
    }
    if (coopCard) {
      const h = coopCard.querySelector("h3");
      const p = coopCard.querySelector("p");
      if (h) h.textContent = "COOP";
      if (p) {
        p.innerHTML = "Same team.<br>If one player dies, everyone loses.";
      }
    }
  }

  document.querySelectorAll(".mpSubModeCard").forEach((el) => {
    const sub = el.getAttribute("data-submode");
    const picked = room.subMode === sub;
    el.classList.toggle("mpSubModeCard--selected", picked);
    (el as HTMLElement).style.pointerEvents = host ? "" : "none";
    (el as HTMLElement).style.cursor = host ? "pointer" : "default";
    (el as HTMLElement).style.opacity = host ? "1" : picked ? "1" : "0.75";
    el.querySelectorAll("button").forEach((btn) => {
      const b = btn as HTMLButtonElement;
      b.disabled = !host;
      if (b.classList.contains("btn") && b.id !== "mpGameModePlayBtn" && b.id !== "mpGameModeBackBtn") {
        b.textContent = picked && host ? "Selected" : "Select";
      }
    });
  });

  const playBtn = document.getElementById("mpGameModePlayBtn") as HTMLButtonElement | null;
  if (playBtn) {
    playBtn.classList.toggle("hidden", !host);
    playBtn.disabled = !host;
  }
  const backBtn = document.getElementById("mpGameModeBackBtn") as HTMLButtonElement | null;
  if (backBtn) {
    backBtn.classList.toggle("hidden", !host);
  }

  const hint = mpSubModeHostHint();
  if (hint) {
    if (host) {
      hint.classList.add("hidden");
    } else {
      hint.textContent =
        room.selectedMode === "speedrun"
          ? "Host is choosing Race or Co-op…"
          : "Host is choosing FFA or Co-op…";
      hint.classList.remove("hidden");
    }
  }
}

function selectSubMode(sub: "ffa" | "coop"): void {
  if (!mpClient.isHost()) return;
  hooks?.playModeCardBeep();
  mpClient.selectSubMode(sub);
  setSubModeError("");
}

async function hostStartGame(origin?: { x: number; y: number } | null): Promise<void> {
  if (!mpClient.isHost()) return;
  const room = mpClient.room;
  if (!room?.subMode) {
    setSubModeError("Select FFA or Co-op.");
    return;
  }
  if (room.players.length < MP_MIN_PLAYERS) {
    setSubModeError(`Need at least ${MP_MIN_PLAYERS} players.`);
    return;
  }
  setSubModeError("");
  const begin = () => {
    mpClient.startGame();
  };
  if (isPlayTransitionEnabled()) runPlayTransition(begin, { origin });
  else begin();
}

function renderLobby(
  room: typeof mpClient.room,
  opts: { skipScreenSync?: boolean } = {},
): void {
  if (!room) return;
  if (!opts.skipScreenSync) syncMpScreensFromRoom(room);

  const codeEl = mpLobbyCode();
  if (codeEl) codeEl.textContent = room.code;

  const waiting = mpLobbyWaiting();
  if (waiting) {
    waiting.classList.toggle("hidden", room.players.length >= MP_MIN_PLAYERS);
  }

  const list = mpLobbyPlayerList();
  if (list) {
    list.replaceChildren();
    for (const p of room.players) {
      const li = document.createElement("li");
      li.className = "mpPlayerRow";
      const name = document.createElement("span");
      name.className = "mpPlayerName";
      name.textContent = p.name + (p.isHost ? " ★ HOST" : "");
      li.appendChild(name);
      if (mpClient.isHost() && p.id !== mpClient.playerId && room.phase === "lobby") {
        const actions = document.createElement("div");
        actions.className = "mpPlayerActions";
        const makeHost = document.createElement("button");
        makeHost.type = "button";
        makeHost.className = "btn mpMakeHostBtn";
        makeHost.textContent = "Make host";
        makeHost.onclick = () => mpClient.transferHost(p.id);
        actions.appendChild(makeHost);
        const kick = document.createElement("button");
        kick.type = "button";
        kick.className = "btn mpKickBtn";
        kick.textContent = "Kick";
        kick.onclick = () => mpClient.kick(p.id);
        actions.appendChild(kick);
        li.appendChild(actions);
      }
      list.appendChild(li);
    }
  }

  document.querySelectorAll(".mpModeCard").forEach((card) => {
    const mode = card.getAttribute("data-mode");
    const selected = room.selectedMode === mode;
    card.classList.toggle("mpModeCard--selected", selected);
    const host = mpClient.isHost();
    const inLobby = room.phase === "lobby";
    (card as HTMLElement).style.pointerEvents = host && inLobby ? "" : "none";
    (card as HTMLElement).style.cursor = host && inLobby ? "pointer" : "default";
    (card as HTMLElement).style.opacity = host && inLobby ? "1" : selected ? "1" : "0.75";
    card.querySelectorAll(".mpSelectModeBtn").forEach((btn) => {
      const b = btn as HTMLButtonElement;
      b.disabled = !host || !inLobby;
      b.textContent = selected && host ? "Selected" : "Select";
    });
  });

  renderGameSettings(room);

  const startBtn = mpLobbyStartBtn();
  if (startBtn) {
    const showPlay = mpClient.isHost() && room.phase === "lobby" && !!room.selectedMode;
    startBtn.classList.toggle("hidden", !showPlay);
    startBtn.disabled = !showPlay;
  }

  const returnLobbyBtn = document.getElementById("mpReturnLobbyBtn");
  if (returnLobbyBtn) {
    returnLobbyBtn.classList.toggle("hidden", !(mpClient.isHost() && room.phase === "results"));
  }

  if (room.phase === "results") {
    mpResultBanner()?.classList.add("hidden");
  } else {
    mpResultBanner()?.classList.add("hidden");
  }
}

function showResultBanner(text: string): void {
  const el = mpResultBanner();
  if (!el) return;
  el.textContent = text;
  el.classList.remove("hidden");
}

/**
 * Close the MP socket when leaving the MP UI for Modes/Hub.
 * Call only from lobby/entry/kick → hub paths — never from in-match return-to-lobby.
 */
function disconnectMpSocketLeavingHub(): void {
  mpClient.disconnect();
}

async function leaveLobby(): Promise<void> {
  mpClient.leaveRoom();
  stopMultiplayerGame();
  remotePlayers.clear();
  leaveMpScreens();
  document.getElementById("mpFloatingLeaveBtn")?.classList.add("hidden");
  disconnectMpSocketLeavingHub();
  hooks?.openModes();
}

async function copyRoomCode(): Promise<void> {
  const code = mpClient.room?.code ?? mpLobbyCode()?.textContent ?? "";
  if (!code) return;
  try {
    await navigator.clipboard.writeText(code);
  } catch {
    /* fallback */
  }
}

function setJoinError(msg: string): void {
  const el = mpJoinError();
  if (!el) return;
  el.textContent = msg;
  el.classList.toggle("hidden", !msg);
}

function setEntryError(msg: string): void {
  const el = mpEntryError();
  if (!el) return;
  el.textContent = msg;
  el.classList.toggle("hidden", !msg);
}

function clearMpErrors(): void {
  setEntryError("");
  setCreateError("");
  setJoinError("");
  setLobbyError("");
  setSubModeError("");
}

function setCreateError(msg: string): void {
  const el = mpCreateError();
  if (!el) return;
  el.textContent = msg;
  el.classList.toggle("hidden", !msg);
}

function setCreateStatus(msg: string): void {
  const el = mpCreateStatus();
  if (!el) return;
  el.textContent = msg;
  el.classList.toggle("hidden", !msg);
}

function setLobbyError(msg: string): void {
  const el = mpLobbyError();
  if (!el) return;
  el.textContent = msg;
  el.classList.toggle("hidden", !msg);
}

function setSubModeError(msg: string): void {
  const el = document.getElementById("mpSubModeError");
  if (!el) return;
  el.textContent = msg;
  el.classList.toggle("hidden", !msg);
}

function resolvePlayerName(): string {
  const fromHook = hooks?.getPlayerName()?.trim();
  if (fromHook) return fromHook.slice(0, 24);
  const stored = localStorage.getItem(GUEST_NAME_KEY);
  if (stored) return stored.slice(0, 24);
  const guest = `Guest${Math.floor(Math.random() * 9000 + 1000)}`;
  localStorage.setItem(GUEST_NAME_KEY, guest);
  return guest;
}

export function endMultiplayerToLobby(): void {
  document.body.classList.remove("mp-results-active");
  hideMpGameOverScreen();
  hideMpSpeedrunResultsScreen();
  stopMultiplayerGame();
  hooks?.stopPlaying();
  showLobby();
}
